/**
 * Doc 03 §4 — the menu-level `design_overrides` layer.
 *
 * Resolution order at render: template.variables → theme package → overrides.
 * The merchant-facing surface shows 6 colour slots + 3 font slots; each slot
 * maps to raw template variable keys (via COLOR_ALIASES) so an override
 * recolors the template bindings, not the questionnaire model.
 *
 * This module owns: the slot registry, the curated 24-font picker list, the
 * zod validation contract, and contrast guidance for the guard rails in §4.3.
 */

import { z } from 'zod';
import { colord } from 'colord';

import { fonts } from '@/lib/types';
import { contrastRatio as colordContrastRatio } from './contrast';

export const DESIGN_COLOR_KEYS = [
  'background',
  'surface',
  'primary',
  'text',
  'price',
  'accent',
  'secondary', 'muted', 'divider', 'promoBg', 'promoText', 'promoBorder',
  'availabilityBg', 'availabilityText', 'hero',
] as const;

export type DesignColorKey = (typeof DESIGN_COLOR_KEYS)[number];

export const DESIGN_FONT_KEYS = ['heading', 'body', 'price', 'display', 'logo'] as const;

export type DesignFontKey = (typeof DESIGN_FONT_KEYS)[number];

/** What the merchant sees — label + the raw template variable slot it writes. */
export const DESIGN_COLOR_SLOTS: Array<{ key: DesignColorKey; label: string }> = [
  { key: 'background', label: 'Background' },
  { key: 'surface', label: 'Cards' },
  { key: 'primary', label: 'Headings' },
  { key: 'text', label: 'Item names' },
  { key: 'price', label: 'Prices' },
  { key: 'accent', label: 'Accent' },
  { key: 'secondary', label: 'Secondary surface' },
  { key: 'muted', label: 'Supporting text' },
  { key: 'divider', label: 'Dividers' },
  { key: 'promoBg', label: 'Promotion background' },
  { key: 'promoText', label: 'Promotion text' },
  { key: 'promoBorder', label: 'Promotion border' },
  { key: 'availabilityBg', label: 'Availability background' },
  { key: 'availabilityText', label: 'Availability text' },
  { key: 'hero', label: 'Hero background' },
];

export const DESIGN_FONT_SLOTS: Array<{ key: DesignFontKey; label: string }> = [
  { key: 'heading', label: 'Heading' },
  { key: 'body', label: 'Body' },
  { key: 'price', label: 'Prices' },
  { key: 'display', label: 'Display' },
  { key: 'logo', label: 'Logo' },
];

export const DESIGN_CONTRAST_PAIRS: Array<{ name: string; fg: DesignColorKey; bg: DesignColorKey }> = [
  { name: 'Item names on Cards', fg: 'text', bg: 'surface' },
  { name: 'Prices on Cards', fg: 'price', bg: 'surface' },
  { name: 'Headings on Background', fg: 'primary', bg: 'background' },
  { name: 'Supporting text on Cards', fg: 'muted', bg: 'surface' },
  { name: 'Promotion text', fg: 'promoText', bg: 'promoBg' },
  { name: 'Availability text', fg: 'availabilityText', bg: 'availabilityBg' },
];

export type FontGroup = 'Display' | 'Serif' | 'Sans' | 'Script' | 'Hand' | 'Other';

export interface CuratedFont {
  name: string;
  css: string;
  group: FontGroup;
}

const CURATED: Record<string, { group: FontGroup }> = {
  // Display
  'Bebas Neue': { group: 'Display' },
  Anton: { group: 'Display' },
  Oswald: { group: 'Display' },
  Righteous: { group: 'Display' },
  'Fugaz One': { group: 'Display' },
  'Gravitas One': { group: 'Display' },
  // Serif
  'Playfair Display': { group: 'Serif' },
  Lora: { group: 'Serif' },
  Merriweather: { group: 'Serif' },
  'Cormorant Garamond': { group: 'Serif' },
  'Crimson Text': { group: 'Serif' },
  Cinzel: { group: 'Serif' },
  // Sans
  Inter: { group: 'Sans' },
  Poppins: { group: 'Sans' },
  Montserrat: { group: 'Sans' },
  Karla: { group: 'Sans' },
  // Script
  'Dancing Script': { group: 'Script' },
  Pacifico: { group: 'Script' },
  'Great Vibes': { group: 'Script' },
  Caveat: { group: 'Script' },
  // Hand
  'Indie Flower': { group: 'Hand' },
  'Permanent Marker': { group: 'Hand' },
  'Rock Salt': { group: 'Hand' },
  'Amatic SC': { group: 'Hand' },
};

/**
 * The curated 24 (of ~154). Each survives `fontSanitation` / `menuThemeFontValue`,
 * which are keyed off the registry in `lib/types/menu.ts`.
 */
export const CURATED_FONTS: CuratedFont[] = fonts
  .filter((font) => CURATED[font.name])
  .map((font) => ({ name: font.name, css: font.css, group: CURATED[font.name].group }));

export const CUSTOM_CURATED_FONT_NAMES: ReadonlySet<string> = new Set(CURATED_FONTS.map((font) => font.name));

/** All registered families are selectable and accepted by the save contract. */
export const DESIGN_FONTS: CuratedFont[] = fonts.map((font) => ({
  ...font, group: CURATED[font.name]?.group || 'Other',
}));
export const DESIGN_FONT_NAMES: ReadonlySet<string> = new Set(fonts.map((font) => font.name));
const VARIABLE_KEY = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/).refine((key) => !['constructor', 'prototype', '__proto__'].includes(key));
export const templateColorValueSchema = z.string().trim().max(500).refine((value) =>
  colord(value).isValid() || ['transparent', 'none'].includes(value) ||
  (/^(linear|radial|conic)-gradient\(/i.test(value) && /^[a-zA-Z0-9#%.,()+\s-]+$/.test(value) && value.endsWith(')')),
  'Use a CSS color or gradient without URLs or variable references.',
);

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const designOverridesSchema = z
  .object({
    colors: z
      .object({
        background: z.string().regex(HEX_COLOR).optional(),
        surface: z.string().regex(HEX_COLOR).optional(),
        primary: z.string().regex(HEX_COLOR).optional(),
        text: z.string().regex(HEX_COLOR).optional(),
        price: z.string().regex(HEX_COLOR).optional(),
        accent: z.string().regex(HEX_COLOR).optional(),
        secondary: z.string().regex(HEX_COLOR).optional(),
        muted: z.string().regex(HEX_COLOR).optional(),
        divider: z.string().regex(HEX_COLOR).optional(),
        promoBg: z.string().regex(HEX_COLOR).optional(),
        promoText: z.string().regex(HEX_COLOR).optional(),
        promoBorder: z.string().regex(HEX_COLOR).optional(),
        availabilityBg: z.string().regex(HEX_COLOR).optional(),
        availabilityText: z.string().regex(HEX_COLOR).optional(),
        hero: z.string().regex(HEX_COLOR).optional(),

      })
      .partial()
      .strict()
      .optional(),
    fonts: z
      .object({
        heading: z.string().refine((value) => DESIGN_FONT_NAMES.has(value), 'Unknown font name.').optional(),
        body: z.string().refine((value) => DESIGN_FONT_NAMES.has(value), 'Unknown font name.').optional(),
        price: z.string().refine((value) => DESIGN_FONT_NAMES.has(value), 'Unknown font name.').optional(),
        display: z.string().refine((value) => DESIGN_FONT_NAMES.has(value), 'Unknown font name.').optional(),
        logo: z.string().refine((value) => DESIGN_FONT_NAMES.has(value), 'Unknown font name.').optional(),
      })
      .partial()
      .strict()
      .optional(),
    templateColors: z.record(VARIABLE_KEY, templateColorValueSchema).refine((values) => Object.keys(values).length <= 150, 'Too many color variables.').optional(),
    templateFonts: z.record(VARIABLE_KEY, z.string().refine((value) => DESIGN_FONT_NAMES.has(value), 'Unknown font name.')).refine((values) => Object.keys(values).length <= 80, 'Too many font variables.').optional(),
    skin: z.string().min(1).max(80).optional(),
  })
  .strict();

export type DesignOverrides = z.infer<typeof designOverridesSchema>;

/**
 * Rejects unknown keys even when the partial groups are empty: an empty body
 * `{}` stays valid (a no-op PATCH = reset), but `{ colors: { booty: "#000" } }`
 * must fail the slot ∈ enum contract from §4.4.
 */
export function isDesignOverrideReset(overrides: DesignOverrides): boolean {
  return !overrides.colors && !overrides.fonts && !overrides.templateColors && !overrides.templateFonts && !overrides.skin;
}

// --- §4.3 Contrast guard ----------------------------------------------------

export function contrastRatio(foreground: string, background: string): number {
  return colordContrastRatio(foreground, background);
}

export function meetsAAContrast(foreground: string, background: string, largeText = false): boolean {
  return contrastRatio(foreground, background) >= (largeText ? 3 : 4.5);
}

export interface ContrastCheck {
  name: string;
  ratio: number;
  passes: boolean;
}

/**
 * Checks the pairs from §4.3 against the *effective* colours: base palette
 * (template + theme result) overlaid with the current override edits, so the
 * warning appears the moment a swatch change breaks its pair.
 */
export function checkDesignContrast(
  overrides: { colors?: Partial<Record<DesignColorKey, string>> },
  baseColors: Partial<Record<DesignColorKey, string>>,
): ContrastCheck[] {
  return DESIGN_CONTRAST_PAIRS.map((pair) => {
    const fg = overrides.colors?.[pair.fg] || baseColors[pair.fg];
    const bg = overrides.colors?.[pair.bg] || baseColors[pair.bg];
    if (!fg || !bg) return { name: pair.name, ratio: NaN, passes: true };
    const ratio = contrastRatio(fg, bg);
    return { name: pair.name, ratio, passes: ratio >= 4.5 };
  });
}

/**
 * "Fix for me" picks the stronger black/white text companion for the current
 * background when the merchant's chosen foreground misses normal-text AA.
 */
export function suggestContrastFix(foreground: string, background: string): string {
  const currentRatio = contrastRatio(foreground, background);
  if (currentRatio >= 4.5) return foreground;
  const blackRatio = contrastRatio('#000000', background);
  const whiteRatio = contrastRatio('#ffffff', background);
  return blackRatio >= whiteRatio ? '#000000' : '#ffffff';
}
